import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AnamnesiTab from './AnamnesiTab';
import PazienteForm from './PazienteForm';
import ProcedureMediche from './ProcedureMediche';
import AlertTab from './AlertTab';
import FotoTab from './FotoTab';
import SchedeProfilo from './SchedeProfilo';
import RegistroRx from './RegistroRx';
import DocumentiTab from './DocumentiTab';
import NotaInline from './NotaInline';
import CompletamentoConsensi from './CompletamentoConsensi';
import LongevityTab from './LongevityTab';
import ProfiloAlimentareTab from './ProfiloAlimentareTab';
import ElencoQuestionari from './ElencoQuestionari';
import AndamentoPeso from './AndamentoPeso';
import EsamiRefertiTab from './EsamiRefertiTab';
import FatturazioneTab from './FatturazioneTab';
import { areeProfilo } from './areeProfilo';
import { pazientiAPI, anamnesiVociAPI, visiteAPI, alertAPI, fotoAPI, rxAPI, dettaglioAPI } from '../../services/api';
import {
  TriangleAlert, Droplet, House, HeartPulse, FileText, BellDot,
  Syringe, Images, Receipt, Watch, Radiation, Mail, Pencil, ChevronLeft,
  Sparkles, Venus, Activity, Footprints, Bone, Accessibility, Dumbbell, ClipboardList,
  ClipboardPlus, ScanSearch, Waves, ChartPie, Ruler, ShieldOff,
  Salad, Stethoscope, Phone, FilePen} from 'lucide-react';
import '../../styles/SchedaPaziente.css';
import ModulisticaWizard from './ModulisticaWizard';
import SceltaFirma from './SceltaFirma';
import api from '../../services/api';
import { polarAPI } from '../../services/polarApi';
import { apriEmail } from '../../utils/posta';
import { confermaUscita } from '../../utils/uscitaProtetta';
import { ascolta } from '../../utils/annunci';
import { apriWhatsApp } from '../../utils/recapiti';
import IconaWhatsApp from '../comuni/IconaWhatsApp';
import RefertiScritti from './RefertiScritti';
import { notificaErrore, notificaAvviso, messaggioDa } from '../../utils/notifiche';


/* data/ora ESATTAMENTE come salvata, senza conversione di fuso */
const fmtDTLocal = (s) => {
  if (!s) return '—';
  const m = String(s).match(/(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return String(s);
  return `${m[3]}/${m[2]}/${m[1]}, ${m[4]}:${m[5]}`;
};


/* ------------------------------------------------------------------ */

const PazienteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [paziente, setPaziente] = useState(null);
  const [loading, setLoading] = useState(true);
  // Voci di allergia attive: alimentano l'avviso in testata.
  const [vociAllergie, setVociAllergie] = useState([]);
  /* Chi arriva dall'agenda dopo aver trasformato un appuntamento in visita
     vuole le procedure mediche, non la home della scheda: la schermata di
     partenza si passa nello state della navigazione, senza aggiungere una
     rotta ne' toccare chi apre la scheda normalmente. */
  const { state: statoNavigazione } = useLocation();
  const [schermata, setSchermataNuda] = useState(statoNavigazione?.schermata || 'home');
  /* Le linguette sono stato locale, non URL: il router non vede il
     cambio e useBlocker non puo' fermarlo. Prima di smontare una
     linguetta con un modulo sporco dentro si chiede qui, con la stessa
     domanda (utils/uscitaProtetta.js). Restare sulla stessa linguetta
     non e' un'uscita. */
  const setSchermata = (s) => {
    if (s !== schermata && !confermaUscita()) return;
    setSchermataNuda(s);
  };
  /* Quando si arriva da un promemoria che indica una visita precisa, la
     scheda non deve solo aprire le procedure: deve aprire QUELLA visita.
     Il valore si consuma una volta sola — passando a un'altra schermata e
     tornando indietro l'elenco resta chiuso, come se ci si fosse arrivati
     a mano, che e' cio' che l'operatore si aspetta. */
  const [visitaDaAprire, setVisitaDaAprire] = useState(statoNavigazione?.visitaId || null);
  /* La linguetta Longevity la vede chi ha la casella «percorso longevity»
     sulla propria scheda medico, piu' amministrazione e segreteria. Chi
     decide e' il server: qui si chiede e basta. Nasconderla non e' la
     protezione - quella sta sulle rotte - e' solo il non mettere in menu
     una cosa che poi risponderebbe 403. */
  const [vedeLongevity, setVedeLongevity] = useState(false);
  /* Il registro RX: l'abilitazione sta sul MEDICO, non sul ruolo, e la
     decide amministrazione o segreteria. Qui si chiede e basta: la
     protezione e' su ogni rotta del registro. */
  const [vedeRx, setVedeRx] = useState(false);
  /* Le aree delle schede cliniche che questo utente deve vedere nel
     profilo medico. `null` finche' la risposta non arriva: con un elenco
     vuoto di partenza le voci lampeggerebbero via e poi tornerebbero. */
  const [areeSchede, setAreeSchede] = useState(null);
  /* I codici delle schede del catalogo che si compilano nel profilo di
     QUESTO paziente. Vuoto di partenza e non `null`: qui il dubbio si
     risolve al contrario delle aree - una voce che compare e poi dice
     «scheda non disponibile» e' peggio di una voce che appare un istante
     dopo. */
  const [schedeProfilo, setSchedeProfilo] = useState([]);
  /* Gli avvisi che vengono dalle schede cliniche: una spunta accesa su un
     campo che porta un allarme. Stanno accanto alle allergie perche' sono
     la stessa cosa per chi legge - qualcosa che va saputo prima di
     toccare il paziente - anche se arrivano da un'altra parte. */
  const [avvisiSchede, setAvvisiSchede] = useState([]);
  // Voci del consenso privacy: da li' escono le persone designate
  const [designate, setDesignate] = useState(null);
  const [hover, setHover] = useState(null);
  /* La sotto-schermata puo' arrivare dalla navigazione, come la schermata:
     un avviso che indica una scheda clinica deve aprire QUELLA, non
     l'anamnesi. */
  const [sottoProfilo, setSottoProfiloNudo] = useState(statoNavigazione?.sottoProfilo || 'anamnesi');
  const setSottoProfilo = (s) => {
    if (s !== sottoProfilo && !confermaUscita()) return;
    setSottoProfiloNudo(s);
  };
  /* E la compilazione precisa dentro quella sotto-schermata. Come
     visitaDaAprire, si consuma una volta sola. */
  const [compilazioneDaAprire] = useState(statoNavigazione?.compilazioneId || null);
  const [showEdit, setShowEdit] = useState(false);   // modale modifica dati
  // Pazienti importati: 'no' chiuso, 'scelta' dove firmare, 'pc' wizard qui
  const [daVerificare, setDaVerificare] = useState('no');
  // Pannello per registrare o revocare l'opposizione all'AI
  const [opposizione, setOpposizione] = useState(false);
  const [anamnesiVoci, setAnamnesiVoci] = useState([]);    // anteprima anamnesi
  /* I tre riquadri scritti nelle procedure (note anamnestiche, esame
     obiettivo, finalita'), visti da qui. Fino al 21 settembre 2026
     l'anteprima leggeva solo le voci firmate: chi scriveva l'esame
     obiettivo nella procedura e tornava al riepilogo non trovava
     niente, e la sezione «Dalle procedure» esisteva solo dentro la
     scheda Anamnesi per intero, dove nessuno andava a cercarla. */
  const [contributi, setContributi] = useState([]);
  const [visiteData, setVisiteData] = useState([]);        // anteprima procedure
  const [alertData, setAlertData] = useState([]);          // anteprima alert
  const [fotoData, setFotoData] = useState([]);            // anteprima foto
  const [openRecap, setOpenRecap] = useState({});          // accordion riepilogo
  const [showCompletamento, setShowCompletamento] = useState(false); // consensi prelievi

  useEffect(() => { loadPaziente(); /* eslint-disable-next-line */ }, [id]);

  /* Le persone designate per la comunicazione dello stato di salute
     stanno nelle voci del consenso privacy, non in anagrafica: le ha
     scelte il paziente firmando, e la' devono restare. Qui si leggono per
     mostrarle, perche' sono le persone a cui si telefona - averle solo
     dentro un PDF firmato vuol dire non averle. */
  useEffect(() => {
    let vivo = true;
    api.get(`/modulistica/consensi/${id}`)
      .then((r) => {
        if (!vivo) return;
        const privacy = (r.data.data.consensi || []).find((c) => c.tipo === 'privacy');
        setDesignate(privacy?.voci || null);
      })
      .catch(() => { /* senza consenso non c'e' niente da mostrare */ });
    return () => { vivo = false; };
  }, [id]);

  useEffect(() => {
    let vivo = true;
    polarAPI.mioAccesso()
      .then(r => { if (vivo) setVedeLongevity(r.data.data.longevity === true); })
      .catch(() => { /* nel dubbio non si mostra */ });
    rxAPI.accesso()
      .then(r => { if (vivo) setVedeRx(r.data.data.vede === true); })
      .catch(() => { /* nel dubbio non si mostra */ });

    /* Se la richiesta fallisce si lascia `null`, e le voci restano tutte:
       nel dubbio si mostra. E' il contrario del Registro RX - li' si
       nasconde una cosa che risponderebbe 403, qui si nasconderebbe una
       scheda che il medico ha tutto il diritto di aprire. */
    api.get('/schede/mie-aree')
      .then(r => { if (vivo) setAreeSchede(r.data?.data?.aree || []); })
      .catch(() => { /* restano tutte */ });
    return () => { vivo = false; };
  }, []);

  /* Quali schede del catalogo si compilano nel profilo. Dipende dal
     paziente solo per la strada - l'elenco e' lo stesso per tutti - ma si
     chiede qui perche' la risposta porta anche quante compilazioni ci sono
     gia', e un domani la voce potra' dirlo. */
  useEffect(() => {
    let vivo = true;
    if (!id) return undefined;
    api.get(`/schede/profilo/${id}`)
      .then(r => {
        if (vivo) setSchedeProfilo(r.data?.data?.schede || []);
      })
      .catch(() => { /* nessuna scheda del catalogo: restano le altre voci */ });
    const leggiAvvisi = () => {
      api.get(`/schede/avvisi/${id}`)
        .then(r => { if (vivo) setAvvisiSchede(r.data?.data?.avvisi || []); })
        .catch(() => { /* senza avvisi la scheda si apre lo stesso */ });
    };
    leggiAvvisi();
    /* Si rilegge a ogni scheda salvata, in qualunque punto del gestionale
       sia successo: chi toglie la spunta della controindicazione deve
       vedere sparire la fascia rossa subito, non al prossimo caricamento
       della pagina. */
    window.addEventListener('rimedichub:scheda-salvata', leggiAvvisi);
    return () => {
      vivo = false;
      window.removeEventListener('rimedichub:scheda-salvata', leggiAvvisi);
    };
  }, [id]);

  const loadPaziente = async () => {
    try {
      setLoading(true);
      const response = await pazientiAPI.getById(id);
      if (response.data.success) {
        const pz = response.data.data.paziente;
        setPaziente(pz);
        // Paziente accettato solo per prelievi: all'apertura della scheda
        // proponi subito il completamento dei consensi mancanti.
        if (pz.tipo_accettazione === 'prelievi') {
            setShowCompletamento(true);
        }
        try {
          // Unica fonte dell'anamnesi: le voci firmate. I vecchi campi
          // di testo non vanno piu' letti, altrimenti l'avviso allergie
          // mostrerebbe un dato che nessuno aggiorna piu'.
          const vociRes = await anamnesiVociAPI.getByPaziente(id);
          const voci = vociRes.data?.data?.voci || [];
          setAnamnesiVoci(voci);
          setVociAllergie(voci.filter((v) => v.sezione === 'allergie' && v.stato === 'attiva'));
        } catch (e) { console.error('Errore caricamento anamnesi', e); }
        try {
          const cRes = await dettaglioAPI.contributi(id);
          setContributi(cRes.data?.data?.contributi || []);
        } catch (e) { console.error('Errore caricamento contributi', e); }
        try {
          const vRes = await visiteAPI.getByPazienteId(id);
          if (vRes.data.success) setVisiteData(vRes.data.data.visite || []);
        } catch (e) { console.error('Errore caricamento procedure', e); }
        try {
          const aRes = await alertAPI.getByPaziente(id);
          if (aRes.data.success) setAlertData(aRes.data.data.alert || []);
        } catch (e) { console.error('Errore caricamento alert', e); }
        try {
          const fRes = await fotoAPI.getByPaziente(id);
          if (fRes.data.success) setFotoData(fRes.data.data.foto || []);
        } catch (e) { console.error('Errore caricamento foto', e); }
      }
    } catch (error) {
      console.error('Errore caricamento paziente:', error);
      notificaErrore(messaggioDa(error, 'Paziente non trovato'));
      navigate('/pazienti');
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------
     La testata si rilegge quando una linguetta cambia i suoi dati.

     Senza questo, la scheda mostrava fino al prossimo F5 quello che
     aveva letto all'apertura: un'allergia appena tolta restava scritta
     nella fascia in testata, un promemoria chiuso risultava aperto, una
     procedura appena registrata non compariva nel riepilogo.

     Sulla fascia delle allergie non e' un conteggio vecchio, e' un
     difetto clinico: mostra un dato che il medico ha appena smentito.
     E' lo stesso che aveva la fascia delle controindicazioni.

     OGNUNO RILEGGE SOLO LA SUA PARTE. Un evento unico «qualcosa e'
     cambiato» costringerebbe a sei richieste ogni volta che si chiude un
     promemoria.

     Gli ascoltatori si staccano all'uscita: senza, ogni riapertura della
     scheda ne aggiunge uno e la stessa lettura parte cinque volte.
     ------------------------------------------------------------------ */
  useEffect(() => {
    let vivo = true;

    const rileggiAnamnesi = () => {
      anamnesiVociAPI.getByPaziente(id)
        .then((r) => {
          if (!vivo) return;
          const voci = r.data?.data?.voci || [];
          setAnamnesiVoci(voci);
          setVociAllergie(voci.filter((v) => v.sezione === 'allergie' && v.stato === 'attiva'));
        })
        .catch(() => { /* resta quello che c'era: meglio di una fascia vuota */ });
    };

    /* I contributi dipendono da tutte e due le cose: da una procedura
       (e' li' che si scrivono) e dall'anamnesi («porta nell'anamnesi»
       li lascia dov'erano ma cambia il quadro accanto). */
    const rileggiContributi = () => {
      dettaglioAPI.contributi(id)
        .then((r) => { if (vivo) setContributi(r.data?.data?.contributi || []); })
        .catch(() => {});
    };

    const rileggiProcedure = () => {
      visiteAPI.getByPazienteId(id)
        .then((r) => { if (vivo && r.data.success) setVisiteData(r.data.data.visite || []); })
        .catch(() => {});
      rileggiContributi();
    };

    const rileggiAlert = () => {
      alertAPI.getByPaziente(id)
        .then((r) => { if (vivo && r.data.success) setAlertData(r.data.data.alert || []); })
        .catch(() => {});
    };

    const rileggiFoto = () => {
      fotoAPI.getByPaziente(id)
        .then((r) => { if (vivo && r.data.success) setFotoData(r.data.data.foto || []); })
        .catch(() => {});
    };

    const stacca = [
      ascolta('anamnesi', rileggiAnamnesi),
      ascolta('anamnesi', rileggiContributi),
      ascolta('procedure', rileggiProcedure),
      ascolta('alert', rileggiAlert),
      ascolta('foto', rileggiFoto),
    ];

    return () => { vivo = false; stacca.forEach((f) => f()); };
  }, [id]);

  /* --- azioni --- */
  const handleEdit = () => setShowEdit(true);          // apre il modale di modifica
  /* Apre la posta della struttura con il destinatario gia' scritto.
     `apriEmail` va chiamata dentro il clic e senza attese in mezzo,
     altrimenti il browser blocca la finestra: il perche' sta in
     utils/posta.js. */
  const handleEmail = () => {
    if (!apriEmail(paziente?.email)) {
      notificaAvviso('Nessuna email registrata per questo paziente');
    }
  };
  /* WhatsApp. `apriWhatsApp` torna null se ha aperto, altrimenti il
     motivo: un numero non ricostruibile NON si indovina, perche' una
     chat aperta sul numero sbagliato manda a un estraneo un messaggio
     che nomina un paziente. */
  const handleWhatsApp = () => {
    const problema = apriWhatsApp(paziente?.telefono);
    if (problema) notificaAvviso(problema);
  };

  /* Il telefono e' un segnaposto dichiarato, non un tasto rotto: il
     centralino non e' ancora collegato. Un tasto che non fa niente e non
     lo dice fa pensare a chi lo preme di aver sbagliato lui - e' lo
     stesso difetto che aveva «Invia email» col mailto. */
  const handleTelefono = () => {
    notificaAvviso('La chiamata dalla scheda non e\' ancora attiva: serve il '
        + 'centralino, che non e\' ancora collegato. Il numero del '
        + 'paziente e\' qui in anagrafica.');
  };

  const handleBack = () => {
    if (schermata !== 'home') setSchermata('home');
    else navigate('/pazienti');
  };

  const calcolaEta = (d) => {
    if (!d) return null;
    const n = new Date(d); if (isNaN(n)) return null;
    const oggi = new Date();
    let eta = oggi.getFullYear() - n.getFullYear();
    const m = oggi.getMonth() - n.getMonth();
    if (m < 0 || (m === 0 && oggi.getDate() < n.getDate())) eta--;
    return eta;
  };

  if (loading) return <div className="sp-loading">Caricamento…</div>;
  if (!paziente) return <div className="sp-loading">Paziente non trovato</div>;

  /* `principale` = i cinque posti dove si va tutto il giorno. Stanno a
     sinistra e grandi il doppio; tutto il resto e' spinto a destra.
     Una barra in cui dodici tasti hanno la stessa importanza non ha
     nessuna importanza: si legge da capo ogni volta.

     Gli incassi stanno qui e non fra le voci minori perche' sono l'unico
     posto della scheda dove si maneggiano soldi: chi li cerca li cerca
     apposta, e non deve scorrere una fila di quadrati uguali. `tinta` da'
     a quel tasto un colore suo - l'unico della barra che non segnala
     "dove ti trovi" ma "che cosa fa". */
  const MENU = [
    { id: 'home',         label: 'Anagrafica',                     icon: House,         tipo: 'schermata', principale: true },
    { id: 'profilo',      label: 'Profilo Medico',                 icon: HeartPulse,      tipo: 'schermata', principale: true },
    { id: 'procedure',    label: 'Visite e procedure mediche',     icon: Syringe,    tipo: 'schermata', principale: true },
    { id: 'foto',         label: 'Foto',                           icon: Images,         tipo: 'schermata', principale: true },
    { id: 'incassi',      label: 'Calcolo incassi e fatturazione', icon: Receipt,        tipo: 'schermata', principale: true, tinta: 'incassi' },
    { id: 'documenti',    label: 'Documenti / Ricette / Consensi', icon: FileText,    tipo: 'schermata' },
    /* I referti SCRITTI qui stanno accanto ai Documenti e non dentro:
       quelli sotto Documenti sono i file RICEVUTI da fuori, e mescolarli
       farebbe sembrare che un referto della clinica e uno di un
       laboratorio siano la stessa cosa. Di uno rispondiamo noi. */
    { id: 'referti',      label: 'Referti scritti qui',            icon: FilePen,     tipo: 'schermata' },
    { id: 'alert',        label: 'Alert',                          icon: BellDot,        tipo: 'schermata' },
    ...(vedeLongevity
      ? [{ id: 'longevity', label: 'Longevity',                    icon: Watch,    tipo: 'schermata' }]
      : []),
    ...(vedeRx
      ? [{ id: 'rx',        label: 'Registro RX',                  icon: Radiation, tipo: 'schermata' }]
      : []),
    { id: 'whatsapp',     label: 'Scrivi su WhatsApp',             icon: IconaWhatsApp, tipo: 'azione', azione: handleWhatsApp, tinta: 'whatsapp' },
    { id: 'telefono',     label: 'Chiama (non ancora attivo)',     icon: Phone,       tipo: 'azione', azione: handleTelefono, spento: true, tinta: 'telefono' },
    { id: 'email',        label: 'Invia Email',                    icon: Mail,        tipo: 'azione', azione: handleEmail },
    { id: 'modifica',     label: 'Modifica Dati',                  icon: Pencil,     tipo: 'azione', azione: handleEdit },
    { id: 'privacy-ai',   label: 'Elaborazione automatizzata',     icon: ShieldOff,  tipo: 'azione', azione: () => setOpposizione(true) },
    { id: 'indietro',     label: 'Indietro',                       icon: ChevronLeft,     tipo: 'azione', azione: handleBack },
  ];

  const onClickTasto = (m) => {
    if (m.tipo === 'schermata') setSchermata(m.id);
    else if (m.azione) m.azione();
  };

  const eta = calcolaEta(paziente.data_nascita);
  const labelSotto = MENU.find(m => m.id === (hover || schermata))?.label;

  const RIEPILOGO = [
    { id: 'profilo',      label: 'Anamnesi / Profilo medico',    target: 'profilo'   },
    { id: 'documenti',    label: 'Documenti, ricette e consensi', target: 'documenti' },
    { id: 'procedure',    label: 'Visite e procedure mediche',    target: 'procedure' },
    { id: 'foto',         label: 'Fotografie',                    target: 'foto'      },
    { id: 'incassi',      label: 'Calcolo incassi',               target: 'incassi'   },
    { id: 'alert',        label: 'Promemoria',            target: 'alert'     },
  ];

  const toggleRecap = (rid) => setOpenRecap(prev => ({ ...prev, [rid]: !prev[rid] }));
  /* Una domanda sola anche quando cambiano linguetta e sotto-linguetta
     insieme: due setter protetti in fila chiederebbero due volte. */
  const vaiA = (target, sotto = target === 'profilo' ? 'anamnesi' : null) => {
    const cambia = target !== schermata || (sotto && sotto !== sottoProfilo);
    if (cambia && !confermaUscita()) return;
    if (sotto) setSottoProfiloNudo(sotto);
    setSchermataNuda(target);
  };

  // anteprima del contenuto per ciascuna voce del riepilogo
  const anteprima = (rid) => {
    if (rid === 'profilo') {
      // Raggruppa le voci attive per sezione: il riepilogo mostra il
      // quadro corrente, lo storico resta nel tab Anamnesi.
      const etichette = {
        allergie: 'Allergie',
        farmaci: 'Farmaci',
        patologie_remote: 'Patologie pregresse',
        familiare: 'Familiare',
        generale: 'Generale',
        note: 'Note',
      };
      const ordine = ['allergie', 'farmaci', 'patologie_remote', 'familiare', 'generale', 'note'];

      const attive = anamnesiVoci.filter((v) => v.stato === 'attiva');
      const campi = ordine
        .map((sez) => {
          const testi = attive.filter((v) => v.sezione === sez).map((v) => v.testo);
          return testi.length ? [etichette[sez], testi.join(' · ')] : null;
        })
        .filter(Boolean);

      if (campi.length === 0 && contributi.length === 0) {
        return <p className="sp-prev-empty">Nessun dato compilato.</p>;
      }
      const taglia = (val) => (String(val).length > 90 ? String(val).slice(0, 90) + '…' : String(val));
      return (
        <div className="sp-prev-list">
          {campi.map(([k, val]) => (
            <div key={k} className="sp-prev-row">
              <span className="sp-prev-key">{k}</span>
              <span className="sp-prev-val">{taglia(val)}</span>
            </div>
          ))}
          {/* Dalle procedure: in sola lettura, con data e medico DELLA
              VISITA e la procedura che li porta. Si modificano dov'e'
              nato il testo; «porta nell'anamnesi» sta nella scheda
              Anamnesi. La riservatezza l'ha gia' applicata il server. */}
          {contributi.length > 0 && (
            <div className="sp-prev-row sp-prev-gruppo">
              <span className="sp-prev-key">Dalle procedure</span>
              <span className="sp-prev-val">{contributi.length === 1 ? '1 contributo' : `${contributi.length} contributi`}</span>
            </div>
          )}
          {contributi.map((c) => (
            <div key={`${c.visita_id}:${c.campo}`} className="sp-prev-row sp-prev-contributo">
              <span className="sp-prev-key">
                {c.data ? new Date(String(c.data).slice(0, 10)).toLocaleDateString('it-IT') : ''}
                {c.medico ? ` · ${c.medico}` : ''}
              </span>
              <span className="sp-prev-val">
                <em>{c.titolo}</em>{c.trattamento ? ` (${taglia(c.trattamento).slice(0, 50)})` : ''}: {taglia(c.testo)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    if (rid === 'procedure') {
      if (!visiteData.length) return <p className="sp-prev-empty">Nessuna procedura registrata.</p>;
      const ultime = [...visiteData]
        .sort((a, b) => new Date(b.data_visita) - new Date(a.data_visita))
        .slice(0, 3);
      return (
        <div className="sp-prev-list">
          <div className="sp-prev-row">
            <span className="sp-prev-key">Totale</span>
            <span className="sp-prev-val">{visiteData.length} procedure</span>
          </div>
          {ultime.map(v => (
            <div key={v.id} className="sp-prev-row">
              <span className="sp-prev-key">{new Date(v.data_visita).toLocaleDateString('it-IT')}</span>
              <span className="sp-prev-val">
                {[(v.trattamento || '').trim(), (v.patologia || '').trim()].filter(Boolean).join(' · ') || 'Procedura'}
              </span>
            </div>
          ))}
        </div>
      );
    }
    if (rid === 'alert') {
      const attivi = (alertData || []).filter(a => a.stato !== 'fatto');
      if (attivi.length === 0) return <p className="sp-prev-empty">Nessun alert attivo.</p>;
      const prossimo = [...attivi].sort((a, b) => new Date(a.data_ora) - new Date(b.data_ora))[0];
      return (
        <div className="sp-prev-list">
          <div className="sp-prev-row">
            <span className="sp-prev-key">Attivi</span>
            <span className="sp-prev-val">{attivi.length}</span>
          </div>
          {prossimo && (
            <div className="sp-prev-row">
              <span className="sp-prev-key">{fmtDTLocal(prossimo.data_ora)}</span>
              <span className="sp-prev-val">{prossimo.titolo}</span>
            </div>
          )}
        </div>
      );
    }
    if (rid === 'foto') {
      if (!fotoData.length) return <p className="sp-prev-empty">Nessun file caricato.</p>;
      const nFoto = fotoData.filter(f => f.categoria === 'foto').length;
      const nVideo = fotoData.filter(f => f.categoria === 'video').length;
      const nDoc = fotoData.filter(f => f.categoria === 'documento').length;
      return (
        <div className="sp-prev-list">
          <div className="sp-prev-row">
            <span className="sp-prev-key">Totale</span>
            <span className="sp-prev-val">{fotoData.length} file</span>
          </div>
          <div className="sp-prev-row">
            <span className="sp-prev-key">Dettaglio</span>
            <span className="sp-prev-val">{nFoto} foto · {nVideo} video · {nDoc} doc</span>
          </div>
        </div>
      );
    }
    return <p className="sp-prev-empty">Anteprima non ancora disponibile per questa sezione.</p>;
  };

  /* Le sotto-schermate servite da una scheda a campi: la voce di menu sta
     a sinistra, l'area del questionario a destra. Quella che resta fuori
     - Foto - non e' una scheda a campi. */
  const AREE_SCHEDE = {
    pelle: 'pelle',
    tricologico: 'tricologia',
    composizione: 'composizione',
    misure: 'misure',
  };

  /* IL PROFILO E' DIVISO IN AREE, NON E' UNA FILA DI ICONE.
     Nove icone in fila si distinguono solo per il disegno, e per sapere
     dove si va bisogna passarci sopra una a una. Raggruppate per area, con
     il titolo sempre scritto sopra, il colpo d'occhio basta: chi cerca la
     tricologia guarda PELLE e non le altre otto.

     FOTO NON C'E' PIU' QUI, e non e' sparita: sta nella barra principale
     del paziente, che e' il posto giusto - le fotografie sono del paziente,
     non di un'area clinica. Averla in due posti voleva solo dire due strade
     per la stessa cosa.

     Un'area senza nessuna voce visibile non si disegna: un riquadro col
     titolo e dentro il vuoto e' peggio del riquadro che non c'e'. */
  /* LE SCHEDE DEL CATALOGO SI DERIVANO, NON SI ELENCANO A MANO.
     Fino al 22 settembre 2026 ogni scheda di schede_struttura era una
     riga scritta qui: la cardiologia e' stata attiva e invisibile per
     tre ore perche' nessuno aveva aggiunto la riga. Adesso
     /api/schede/profilo/:id dice quali schede sono attive, con area,
     nome e ordine, e le voci nascono da li'. Quello che resta scritto
     a mano e' solo COME disegnarle: il titolo del gruppo per un'area
     (per unire ortopedia, fisiatria e fisioterapia, e per mettere la
     medicina estetica sotto Pelle) e l'icona; un'area nuova senza voce
     qui esce col suo nome e un'icona generica - visibile, non sparita. */
  const AREE_PROFILO = areeProfilo(schedeProfilo);

  const SUB_PROFILO = AREE_PROFILO.flatMap((a) => a.voci);

  /* Le voci che cominciano per `scheda_` sono schede del catalogo dei
     campi, e compaiono solo se quella scheda esiste, e' attiva e ha dei
     campi dentro: l'elenco arriva dal server (`schedeProfilo`), cosi' una
     scheda nuova appare senza toccare questa schermata. */
  /* ATTENZIONE: non c'e' piu' un ramo di ripiego in fondo alla catena qui
     sotto. Ogni id elencato qui DEVE avere il suo ramo, altrimenti l'area
     resta vuota senza dire niente. Prima il ripiego c'era ed era il
     riquadro «In arrivo», ma lo raggiungeva una voce sola. */
  /* Le voci servite da una scheda clinica compaiono solo se quella scheda
     e' fra quelle di questo medico. Le altre - anamnesi, foto, esami,
     profilo alimentare - non sono schede della struttura e restano
     sempre. */
  const AREA_DI = { ...AREE_SCHEDE, gastro: 'gastroenterologia' };
  const subVisibili = SUB_PROFILO.filter((v) => {
    if (v.id.startsWith('scheda_')) return true;   // derivate: ci sono solo se attive
    const area = AREA_DI[v.id];
    if (!area || areeSchede === null) return true;
    return areeSchede.includes(area);
  });
  const subLabel = SUB_PROFILO.find(s => s.id === sottoProfilo)?.label;

  return (
    <div className="sp-container">

      {/* ---------- HEADER ---------- */}
      <div className="sp-header">
        <span className="sp-pid">{paziente.id_paziente}</span>
        <span className="sp-sex">{paziente.sesso === 'M' ? '♂' : '♀'}</span>
        {paziente.tipo_accettazione === 'prelievi' && (
          <span className="rm-stato rm-stato-attenzione" title="Consensi non completi">Prelievi</span>
        )}
        {paziente.longevity === true && (
          <span className="rm-stato rm-stato-accento">Longevity</span>
        )}
        {paziente.opposizione_ai === true && (
          <span className="rm-stato rm-stato-neutro"
                title="Il paziente ha chiesto che i suoi dati non siano elaborati con strumenti di intelligenza artificiale">
            No AI
          </span>
        )}
        <span className="sp-name">{paziente.nome} {paziente.cognome}</span>
        {eta != null && <span className="sp-age">{eta} anni</span>}
        {paziente.telefono && <span className="sp-tel">{paziente.telefono}</span>}
      </div>

      {/* ---------- PAZIENTE IMPORTATO ---------- */}
      {paziente.anagrafica_verificata === false && (
        <div className="sp-verifica-banner">
          <div>
            <b>Anagrafica da completare</b>
            <p>
              Questo paziente proviene dal vecchio archivio: i suoi recapiti
              potrebbero mancare e i consensi sono ancora quelli cartacei.
              Fai controllare i dati e firmare i moduli nuovi.
            </p>
          </div>
          <button onClick={() => setDaVerificare('scelta')}>
            Completa la pratica
          </button>
        </div>
      )}

      {/* ---------- AVVISO ALLERGIE ---------- */}
      {vociAllergie.length > 0 && (
        <div className="sp-allergie-banner"
             onClick={() => vaiA('profilo')}
             role="button" tabIndex={0}
             onKeyDown={(e) => { if (e.key === 'Enter') vaiA('profilo'); }}>
          <TriangleAlert size={22} strokeWidth={2} aria-hidden="true" />
          <div className="sp-allergie-testo">
            <span className="sp-allergie-titolo">Allergie</span>
            <span className="sp-allergie-elenco">
              {vociAllergie.map((v, i) => (
                <span key={v.id || i} className="sp-allergia">
                  {v.testo}
                  {v.gravita && <em className={`sp-allergia-gravita gravita-${v.gravita}`}>{v.gravita}</em>}
                </span>
              ))}
            </span>
          </div>
        </div>
      )}

      {/* ---------- AVVISI DALLE SCHEDE CLINICHE ----------
          Stessa forma delle allergie, e non e' pigrizia: per chi legge
          sono la stessa cosa - qualcosa da sapere prima di toccare il
          paziente - e due forme diverse per la stessa urgenza vogliono
          dire che una delle due si impara a ignorare. */}
      {avvisiSchede.length > 0 && (
        <div className="sp-allergie-banner sp-avviso-scheda">
          <TriangleAlert size={22} strokeWidth={2} aria-hidden="true" />
          <div className="sp-allergie-testo">
            <span className="sp-allergie-titolo">Attenzione</span>
            <span className="sp-allergie-elenco">
              {avvisiSchede.map((a) => (
                <span key={a.campo_id} className="sp-allergia">
                  {a.allarme_testo}
                  <em className="sp-allergia-gravita">{a.scheda}</em>
                </span>
              ))}
            </span>
          </div>
        </div>
      )}

      {/* ---------- BANNER CONSENSI DA COMPLETARE ---------- */}
      {paziente.tipo_accettazione === 'prelievi' && (
        <div className="sp-banner-prelievi" onClick={() => setShowCompletamento(true)}>
          <span className="sp-banner-goccia"><Droplet size={16} strokeWidth={1.8} aria-hidden="true" /></span>
          <span>Paziente accettato solo per il punto prelievi — consensi da completare.</span>
          <button className="sp-banner-btn" onClick={(e) => { e.stopPropagation(); setShowCompletamento(true); }}>
            Completa consensi
          </button>
        </div>
      )}

      {/* ---------- BARRA ICONE ---------- */}
      <div className="sp-iconbar" onMouseLeave={() => setHover(null)}>
        {['principali', 'altre'].map(gruppo => (
          <div className={`sp-icongruppo sp-icongruppo--${gruppo}`} key={gruppo}>
            {MENU.filter(m => (gruppo === 'principali' ? m.principale : !m.principale)).map(m => {
              const IconComp = m.icon;
              const attivo = m.tipo === 'schermata' && schermata === m.id;
              return (
                <button key={m.id}
                  className={`sp-iconbtn ${m.principale ? 'grande' : ''} ${attivo ? 'attivo' : ''} ${m.tipo === 'azione' ? 'azione' : ''} ${m.tinta ? 'sp-iconbtn--' + m.tinta : ''} ${m.spento ? 'spento' : ''}`}
                  onClick={() => onClickTasto(m)}
                  onMouseEnter={() => setHover(m.id)}
                  title={m.label}>
                  <IconComp size={m.principale ? 36 : 24} />
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="sp-iconlabel">{labelSotto}</div>

      {/* ---------- CORPO ---------- */}
      <div className="sp-body">

        {schermata === 'home' && (
          <div className="sp-recap">
            <div className="sp-card">
              <div className="sp-card-head">Dati Paziente</div>
              <div className="sp-card-body">
                <Riga label="Codice fiscale" value={paziente.codice_fiscale} />
                <Riga label="Data di nascita"
                      value={paziente.data_nascita ? new Date(paziente.data_nascita).toLocaleDateString('it-IT') : 'N/A'} />
                <Riga label="Luogo di nascita" value={paziente.luogo_nascita} />
                <Riga label="Sesso" value={paziente.sesso === 'M' ? 'Maschio' : 'Femmina'} />
                <div className="sp-sep" />
                <Riga label="Telefono" value={paziente.telefono} />
                <Riga label="Email" value={paziente.email} />
                <div className="sp-sep" />
                <Riga label="Indirizzo" value={paziente.indirizzo} />
                <Riga label="Città" value={paziente.citta} />
                <Riga label="CAP" value={paziente.cap} />
                <Riga label="Provincia" value={paziente.provincia} />
                <div className="sp-sep" />

                {/* Chi chiamare, secondo il consenso firmato. Non si
                    modifica da qui: e' una scelta che il paziente ha
                    firmato, e cambiarla in anagrafica renderebbe il PDF
                    archiviato diverso da cio' che il gestionale mostra.
                    Per cambiarla si rifa' il consenso. */}
                <div className="sp-row sp-row-blocco">
                  <span className="sp-row-label">Persone di riferimento</span>
                  <div className="sp-row-valore">
                    {designate?.familiari !== true ? (
                      <span className="sp-vuoto-nota">
                        {designate
                          ? 'Il paziente non ha autorizzato la comunicazione a terzi'
                          : 'Consenso privacy non ancora firmato'}
                      </span>
                    ) : (
                      <>
                        {[1, 2].map((n) => {
                          const nome = n === 1 ? designate.familiare_nome : designate.familiare2_nome;
                          if (!nome) return null;
                          const rel = n === 1 ? designate.familiare_relazione : designate.familiare2_relazione;
                          const rec = n === 1 ? designate.familiare_recapito : designate.familiare2_recapito;
                          return (
                            <div key={n} className="sp-designata">
                              <b>{nome}</b>
                              {rel && <span className="sp-designata-rel">{rel}</span>}
                              {rec && <span className="sp-designata-rec">{rec}</span>}
                            </div>
                          );
                        })}
                        {!designate.familiare_nome && (
                          <span className="sp-vuoto-nota">Autorizzata, ma nessun nome indicato</span>
                        )}
                        <span className="sp-vuoto-nota">
                          dal consenso privacy: si cambia rifacendo il consenso
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Il medico curante: campo suo dal 4 settembre 2026, perche'
                    e' UNO, lo dichiara il paziente in accettazione e serve
                    poterlo leggere da qui senza cercarlo in mezzo a un testo
                    libero. La spunta «non ne ha» distingue il paziente senza
                    curante da quello a cui nessuno l'ha chiesto. */}
                <div className="sp-row">
                  <span className="sp-row-label">Medico curante</span>
                  <span className="sp-row-value">
                    {paziente.medico_curante_assente
                      ? <em className="sp-assente">il paziente ha dichiarato di non averne</em>
                      : paziente.medico_curante
                        ? <>
                            {paziente.medico_curante}
                            {paziente.medico_curante_contatto
                              && <span className="sp-curante-contatto">
                                   {paziente.medico_curante_contatto}
                                 </span>}
                          </>
                        : <em className="sp-assente">non richiesto</em>}
                  </span>
                </div>

                {/* Gli altri professionisti esterni. Si chiamava «Medici di
                    riferimento» e conteneva anche il curante: ora sono due
                    campi, e questo resta il testo libero per il resto. */}
                <div className="sp-row sp-row-note-edit">
                  <span className="sp-row-label">Altri professionisti</span>
                  <div className="sp-row-note-campo">
                    <NotaInline
                      pazienteId={paziente.id}
                      valoreIniziale={paziente.altri_professionisti}
                      salva={pazientiAPI.updateAltriProfessionisti}
                      placeholder="Specialisti, fisioterapisti, altri professionisti…"
                      righe={2}
                      onSaved={(nuovo) => setPaziente((p) => ({ ...p, altri_professionisti: nuovo }))}
                    />
                  </div>
                </div>

                <div className="sp-row sp-row-note-edit">
                  <span className="sp-row-label">Note</span>
                  <div className="sp-row-note-campo">
                    <NotaInline
                      pazienteId={paziente.id}
                      valoreIniziale={paziente.note}
                      onSaved={(nuovo) => setPaziente((p) => ({ ...p, note: nuovo }))}
                    />
                  </div>
                </div>
              </div>
              <button className="sp-btn-outline" onClick={handleEdit}>Modifica dati</button>
            </div>

            <div className="sp-card">
              <div className="sp-card-head sp-card-head--accent">Riepilogo Scheda Paziente</div>
              <div className="sp-card-body sp-recap-list">
                {RIEPILOGO.map(v => (
                  <div key={v.id} className={`sp-recap-acc ${openRecap[v.id] ? 'open' : ''}`}>
                    <button className="sp-recap-item" onClick={() => toggleRecap(v.id)}>
                      <span>{v.label}</span>
                      <span className="sp-recap-arrow">›</span>
                    </button>
                    {openRecap[v.id] && (
                      <div className="sp-recap-panel">
                        {anteprima(v.id)}
                        <button className="sp-btn-modifica" onClick={() => vaiA(v.target)}>
                          Modifica ora
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {schermata === 'profilo' && (
          <div className="sp-screen">
            <div className="sp-subtitle">Profilo Medico del Paziente</div>

            <div className="sp-aree">
              {AREE_PROFILO.map((area) => {
                const voci = area.voci.filter((v) => subVisibili.some((x) => x.id === v.id));
                if (!voci.length) return null;
                return (
                  <div className="sp-area" key={area.titolo}>
                    <div className="sp-area-titolo">{area.titolo}</div>
                    <div className="sp-area-icone">
                      {voci.map((s) => {
                        const IconComp = s.icon;
                        const attivo = sottoProfilo === s.id;
                        return (
                          <button key={s.id}
                            className={`sp-subbtn ${attivo ? 'attivo' : ''}`}
                            onClick={() => setSottoProfilo(s.id)}
                            title={s.label}>
                            <IconComp />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Qui stava «+ Nuovo Referto», che apriva un alert con scritto
                «in arrivo nel prossimo blocco». Compariva su Foto, Esami e
                Referti e Diario clinico, cioe' per esclusione su tutto quello
                che non e' una scheda a campi.

                Tolto il 12 settembre 2026. Un pulsante che non fa niente non e'
                neutro: chi lo preme la prima volta perde tempo, chi lo preme la
                seconda smette di fidarsi anche dei pulsanti che funzionano.
                Lo stesso giorno, e per la stessa ragione, e' sparita la voce
                «Diario Clinico / Note»: le note sul paziente stanno gia' nella
                scheda anagrafica.

                Quando il referto si potra' davvero creare, il pulsante torna —
                e allora fara' qualcosa. */}
            <div className="sp-sublabel">
              <span>{subLabel}</span>
            </div>

            {sottoProfilo.startsWith('scheda_')
              ? <SchedeProfilo pazienteId={id} schedaCodice={sottoProfilo} />
              : sottoProfilo === 'anamnesi'
              ? <AnamnesiTab pazienteId={id} sessoPaziente={paziente.sesso} />
              : sottoProfilo === 'sangue'
              ? <EsamiRefertiTab pazienteId={id} />
              : sottoProfilo === 'alimentare'
              ? <ProfiloAlimentareTab pazienteId={id}
                                      apriCompilazione={compilazioneDaAprire} />
              : sottoProfilo === 'gastro'
              ? <ElencoQuestionari pazienteId={id} area="gastroenterologia" etichetta="scheda"
                                   apriCompilazione={compilazioneDaAprire} />
              /* Le quattro schede cliniche stanno sulla stessa struttura
                 della gastroenterologica: sezioni, campi tipizzati,
                 intervalli. Una seconda impalcatura per ognuna avrebbe
                 voluto dire quattro schermate da tenere allineate a mano. */
              : AREE_SCHEDE[sottoProfilo]
              ? <>
                  {/* L'andamento del peso sta SOPRA l'elenco delle
                      compilazioni, e solo sulle due schede nutrizionali:
                      e' la domanda che ci si fa aprendo la scheda -
                      «come sta andando» - e l'elenco delle date da sola
                      non la risponde. Il riquadro non compare finche'
                      non c'e' almeno una misura. */}
                  {(sottoProfilo === 'misure' || sottoProfilo === 'composizione') && (
                    <AndamentoPeso pazienteId={id} />
                  )}
                  <ElencoQuestionari pazienteId={id}
                                     area={AREE_SCHEDE[sottoProfilo]}
                                     etichetta="scheda"
                                     apriCompilazione={compilazioneDaAprire} />
                </>
              : null}
          </div>
        )}

        {schermata === 'documenti'  && <div className="sp-screen"><DocumentiTab pazienteId={id} /></div>}
        {schermata === 'referti'    && <div className="sp-screen"><RefertiScritti pazienteId={id} /></div>}
        {schermata === 'alert'      && <div className="sp-screen"><AlertTab pazienteId={id} /></div>}
        {schermata === 'procedure'  && <div className="sp-screen"><ProcedureMediche pazienteId={id} sessoPaziente={paziente.sesso} visitaDaAprire={visitaDaAprire} onVisitaAperta={() => setVisitaDaAprire(null)} /></div>}
        {schermata === 'foto'       && <div className="sp-screen"><FotoTab pazienteId={id} /></div>}
        {schermata === 'incassi'    && <div className="sp-screen"><FatturazioneTab pazienteId={id} /></div>}
        {schermata === 'longevity' && vedeLongevity && <div className="sp-screen"><LongevityTab pazienteId={id} /></div>}
        {schermata === 'rx'        && vedeRx        && <div className="sp-screen"><RegistroRx pazienteId={id} /></div>}

      </div>

      {/* ---------- MODALE MODIFICA DATI ---------- */}
      {showEdit && (
        <PazienteForm
          paziente={paziente}
          onClose={() => setShowEdit(false)}
          onSuccess={loadPaziente}
        />
      )}

      {showCompletamento && paziente.tipo_accettazione === 'prelievi' && (
        <CompletamentoConsensi
          paziente={paziente}
          onClose={() => setShowCompletamento(false)}
          onSuccess={() => { setShowCompletamento(false); loadPaziente(); }}
        />
      )}
      {/* ---------- OPPOSIZIONE ALL'ELABORAZIONE AUTOMATIZZATA ---------- */}
      {opposizione && (
        <div className="sp-opp-sfondo" onClick={() => setOpposizione(false)}>
          <div className="sp-opp" onClick={(e) => e.stopPropagation()}>
            <h3>Elaborazione con strumenti di intelligenza artificiale</h3>

            {paziente.opposizione_ai ? (
              <>
                <p>
                  Questo paziente ha chiesto che i suoi dati non siano elaborati
                  con strumenti di intelligenza artificiale
                  {paziente.opposizione_ai_il &&
                    ` dal ${new Date(paziente.opposizione_ai_il).toLocaleDateString('it-IT')}`}.
                  Le relative attività vanno svolte manualmente.
                </p>
                <div className="sp-opp-azioni">
                  <button onClick={() => setOpposizione(false)}>Chiudi</button>
                  <button className="sp-opp-revoca" onClick={async () => {
                    try {
                      await api.post(`/ai/opposizione/${paziente.id}`, { opposto: false });
                      setOpposizione(false);
                      loadPaziente();
                    } catch (e) {
                      notificaErrore(messaggioDa(e, 'Non riesco a revocare l\'opposizione'));
                    }
                  }}>
                    Revoca l'opposizione
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>
                  Registra la richiesta del paziente di non essere sottoposto a
                  elaborazione mediante strumenti di intelligenza artificiale,
                  come previsto dall'informativa. Da quel momento il sistema
                  rifiuterà ogni elaborazione che riguardi i suoi dati.
                </p>
                <div className="sp-opp-azioni">
                  <button onClick={() => setOpposizione(false)}>Annulla</button>
                  <button className="sp-opp-conferma" onClick={async () => {
                    try {
                      await api.post(`/ai/opposizione/${paziente.id}`, { opposto: true });
                      setOpposizione(false);
                      loadPaziente();
                    } catch (e) {
                      notificaErrore(messaggioDa(e, 'Non riesco a registrare l\'opposizione'));
                    }
                  }}>
                    Registra l'opposizione
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Dove firma il paziente: stesso meccanismo dei consensi di
          procedura, con l'elenco dei tablet disponibili. */}
      {daVerificare === 'scelta' && (
        <SceltaFirma
          pazienteId={paziente.id}
          consensi={[]}
          onSuPc={() => setDaVerificare('pc')}
          onCompletato={() => { setDaVerificare('no'); loadPaziente(); }}
          onAnnulla={() => setDaVerificare('no')}
        />
      )}

      {daVerificare === 'pc' && (
        <ModulisticaWizard
          modalita={paziente.tipo_accettazione === 'prelievi' ? 'prelievi' : 'completo'}
          pazienteEsistente={paziente}
          onClose={() => setDaVerificare('no')}
          onSuccess={() => { setDaVerificare('no'); loadPaziente(); }}
        />
      )}

    </div>
  );
};

const Riga = ({ label, value, evidenzia }) => (
  <div className={`sp-row${evidenzia ? ' sp-row-note' : ''}`}>
    <span className="sp-row-label">{label}</span>
    <span className="sp-row-value">{value || 'N/A'}</span>
  </div>
);

const Placeholder = ({ titolo }) => (
  <div className="sp-screen sp-placeholder">
    <div className="sp-placeholder-badge">In costruzione</div>
    <h3>{titolo}</h3>
    <p>Questa sezione verrà attivata nel prossimo blocco di sviluppo.</p>
  </div>
);

export default PazienteDetail;
